import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Typography,
    IconButton,
    Button,
    Avatar,
    Paper,
    Divider,
    List,
    ListItem,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    Card,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Breadcrumbs,
    Link,
    Grid,
    Tooltip,
    CircularProgress,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import FolderIcon from "@mui/icons-material/Folder"; // File explorer folder icon
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import DescriptionIcon from "@mui/icons-material/Description";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import AssignmentIcon from "@mui/icons-material/Assignment";
import dayjs, { Dayjs } from "dayjs";
import isToday from "dayjs/plugin/isToday";
import meetingData from "./meetingData.json";
import { useAppDispatch, useAppSelector } from "../../../../Redux/Store/Hooks";
import { getLibraryScheduledClassesAction } from "../../../../Redux/Slices/ScheduledClasses/LibraryScheduledClassesSlice";
import { getWorksheetsAction, getStudyMaterialsAction, clearLibraryFiles } from "../../../../Redux/Slices/ScheduledClasses/LibraryFilesSlice";
import api from "../../../../Redux/Axios/middleware";

dayjs.extend(isToday);

interface Meeting {
    id: string;
    title: string;
    date: string;
    video: {
        name: string;
        url: string;
    };
    booklet: {
        name: string;
        url: string;
    };
    notes: {
        name: string;
        url: string;
    };
    worksheet: {
        name: string;
        url: string;
    };
    // Derived properties for UI
    startTime?: Dayjs;
    endTime?: Dayjs;
}

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const DAYS_OF_WEEK = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
const PAST_COLOR = "#fafafa";

const MeetingRecordsPage = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));      // < 600px
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md')); // 600–900px
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));       // > 900px

    const [view, setView] = useState<"calendar" | "files">("calendar");
    const [currentDate, setCurrentDate] = useState(dayjs()); // Mini cal view
    const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs()); // Main grid view
    const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);
    const [isChoiceModalOpen, setIsChoiceModalOpen] = useState(false);
    const [meetingForChoice, setMeetingForChoice] = useState<Meeting | null>(null);
    const [fileTypeFilter, setFileTypeFilter] = useState<"video" | "notes" | "worksheet" | "all">("all");
    const [explorerPath, setExplorerPath] = useState<"root" | "notes" | "video" | "booklet" | "worksheet">("root");
    const [currentMonth, setCurrentMonth] = useState(dayjs());
    const [expanded, setExpanded] = useState<string | false>('calendar');
    const scrollRef = useRef<HTMLDivElement>(null);
    
    // Redux selectors for files
    const { 
        worksheets: worksheetData, 
        studyMaterials: studyMaterialsData, 
        isLoadingWorksheets, 
        isLoadingStudyMaterials 
    } = useAppSelector((state) => state.LibraryFiles);
    
    const isLoadingFiles = isLoadingWorksheets || isLoadingStudyMaterials;

    const dispatch = useAppDispatch();
    const { data: listData, isLoading } = useAppSelector((state) => state.LibraryScheduledClasses);

    useEffect(() => {
        dispatch(getLibraryScheduledClassesAction());
    }, [dispatch]);

    // Flatten and enrich data with dummy times for the calendar grid
    const processedMeetings: Meeting[] = React.useMemo(() => {
        let sourceData: any[] = [];
        
        if (listData && Array.isArray(listData.data)) {
             sourceData = listData.data;
        } else if (Array.isArray(listData)) {
             sourceData = listData;
        } else {
             // Fallback or empty if needed.
             // If we strict to ONLY use API: sourceData = [];
             // But let's fallback to meetingData if something goes incredibly wrong or pending.
             sourceData = meetingData.flatMap((monthGroup: any) => monthGroup.meetings);
        }

        return sourceData.map((m: any, index: number) => {
            const dateStr = m.date || m.start_time || m.scheduled_date || dayjs().format("YYYY-MM-DD");
            return {
                id: m.id ? String(m.id) : String(index),
                title: m.title || m.class_title || m.class_name || m.subject || "Scheduled Class",
                date: dateStr,
                video: m.video || { name: m.video_name || "Video", url: m.video_url || "#" },
                booklet: m.booklet || { name: m.booklet_name || "Booklet", url: m.booklet_url || "#" },
                notes: m.notes || { name: m.notes_name || "Notes", url: m.notes_url || "#" },
                worksheet: m.worksheet || { name: m.worksheet_name || "Worksheet", url: m.worksheet_url || "#" },
                // Allow API to override exact times, else fallback to demo logic
                startTime: m.start_time ? dayjs(m.start_time) : dayjs(dateStr).hour(10 + (parseInt(m.id || String(index)) % 4)).minute(0),
                endTime: m.end_time ? dayjs(m.end_time) : dayjs(dateStr).hour(11 + (parseInt(m.id || String(index)) % 4)).minute(30)
            };
        });
    }, [listData]);

    const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpanded(isExpanded ? panel : 'calendar');
    };

    useEffect(() => {
        if (scrollRef.current && view === "calendar") {
            scrollRef.current.scrollTop = 48 * 8; // Scroll to 8 AM
        }
    }, [view]);

    const handleBack = () => {
        if (view === "files") {
            setView("calendar");
            setSelectedMeeting(null);
            setFileTypeFilter("all");
            setExplorerPath("root");
            dispatch(clearLibraryFiles());
        }
    };

    const handleDownload = (file: any) => {
        if (file.driveFileId) {
            // Use Google Drive direct download link
            const downloadUrl = `https://docs.google.com/uc?export=download&id=${file.driveFileId}`;
            window.open(downloadUrl, "_blank");
        } else if (file.url && file.url !== "#") {
            window.open(file.url, "_blank");
        }
    };

    const handleCalendarMeetingClick = (meeting: Meeting) => {
        setMeetingForChoice(meeting);
        setIsChoiceModalOpen(true);
    };

    const handleChoiceSelection = async (type: "video" | "notes" | "worksheet") => {
        if (meetingForChoice) {
            const meetingId = meetingForChoice.id;
            setSelectedMeeting(meetingForChoice);
            setFileTypeFilter(type);
            setView("files");
            
            if (type === "worksheet") {
                setExplorerPath("worksheet");
                // Only dispatch worksheets action for worksheet selection
                dispatch(getWorksheetsAction(meetingId));
            } else if (type === "notes") {
                setExplorerPath("root"); // Show folders first for study materials
                // Only dispatch study materials action for notes selection
                dispatch(getStudyMaterialsAction(meetingId));
            } else {
                setExplorerPath("root"); // Show folders first for video
                // For video, dispatch both to have all files available
                dispatch(getWorksheetsAction(meetingId));
                dispatch(getStudyMaterialsAction(meetingId));
            }
            
            setIsChoiceModalOpen(false);
            setMeetingForChoice(null);
        }
    };

    // Mini calendar logic
    const startOfMonth = currentDate.startOf("month");
    const endOfMonth = currentDate.endOf("month");
    const miniCalDays: (Dayjs | null)[] = [];
    const firstDayIndex = startOfMonth.day();
    for (let i = 0; i < firstDayIndex; i++) miniCalDays.push(null);
    for (let i = 1; i <= endOfMonth.date(); i++) miniCalDays.push(startOfMonth.date(i));

    // Week view logic
    const visibleDays = isMobile ? 1 : isTablet ? 3 : 7;
    const startOfWeek = isDesktop ? selectedDate.startOf("week") : selectedDate;
    const weekDays = Array.from({ length: visibleDays }, (_, i) => startOfWeek.add(i, "day"));

    const nextWeek = () => setSelectedDate(selectedDate.add(1, isDesktop ? "week" : "day"));
    const prevWeek = () => setSelectedDate(selectedDate.subtract(1, isDesktop ? "week" : "day"));
    const goToToday = () => {
        setSelectedDate(dayjs());
        setCurrentDate(dayjs());
    };

    const renderFiles = () => {
        if (!selectedMeeting) return null;

        const folders = [
            { id: "booklet", name: "Booklets", icon: <FolderIcon sx={{ fontSize: 80, color: "#ffb300" }} />, type: "booklet", count: studyMaterialsData.length }, // Assuming all for now or needs filtering
            { id: "notes", name: "Study Notes", icon: <FolderIcon sx={{ fontSize: 80, color: "#ffb300" }} />, type: "notes", count: studyMaterialsData.length },
        ];

        if (selectedMeeting.endTime?.isBefore(dayjs())) {
            folders.push({ id: "video", name: "Video Lesson", icon: <FolderIcon sx={{ fontSize: 80, color: "#ea4335" }} />, type: "video", count: 1 });
        }

        const getFiles = () => {
            if (explorerPath === "booklet" || explorerPath === "notes") {
                const results = studyMaterialsData.map((file: any) => ({
                    type: explorerPath,
                    name: file.drive_file_name || file.name || file.title || (explorerPath === "booklet" ? "Work Booklet" : "Study Materials"),
                    fileName: file.drive_file_name || file.file_name || file.name || file.title || "document.pdf",
                    icon: <DescriptionIcon sx={{ fontSize: 48, color: explorerPath === "booklet" ? "#38a169" : "#3182ce" }} />,
                    url: file.drive_file_url || file.url || file.file_url || "#",
                    driveFileId: file.drive_file_id,
                    downloadable: !!file.downloadable || !!file.drive_file_id
                }));
                return results;
            }
            if (explorerPath === "worksheet") {
                const results = worksheetData.map((file: any) => ({
                    type: "worksheet",
                    name: file.drive_file_name || file.name || file.title || "Interactive Worksheet",
                    fileName: file.drive_file_name || file.file_name || file.name || file.title || "worksheet.pdf",
                    icon: <AssignmentIcon sx={{ fontSize: 48, color: "#805ad5" }} />,
                    url: file.drive_file_url || file.url || file.file_url || "#",
                    driveFileId: file.drive_file_id,
                    downloadable: true
                }));
                return results;
            }
            if (explorerPath === "video") {
                return selectedMeeting.video ? [{
                    type: "video",
                    name: "Recorded Video",
                    fileName: selectedMeeting.video.name,
                    icon: <VideoLibraryIcon sx={{ fontSize: 48, color: "#e53e3e" }} />,
                    url: selectedMeeting.video.url,
                    downloadable: false
                }] : [];
            }
            return [];
        };

        const currentFiles = getFiles();

        return (
            <Box sx={{ p: 4, bgcolor: "#f8fafc", minHeight: "100vh" }}>
                 <Box sx={{ mb: 4 }}>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: "#1a202c", mb: 1 }}>
                        {selectedMeeting.title}
                    </Typography>
                    <Breadcrumbs separator={<KeyboardArrowRightIcon fontSize="small" />} sx={{ mb: 2 }}>
                        <Link underline="hover" color="inherit" sx={{ cursor: "pointer" }} onClick={() => setView("calendar")}>
                            Library
                        </Link>
                        <Link underline="hover" color="inherit" sx={{ cursor: "pointer" }} onClick={() => setExplorerPath("root")}>
                            {selectedMeeting.title}
                        </Link>
                        {explorerPath !== "root" && (
                            <Typography color="text.primary">
                                {explorerPath === "booklet" ? "Booklets" : explorerPath === "notes" ? "Study Notes" : "Video Lesson"}
                            </Typography>
                        )}
                    </Breadcrumbs>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <IconButton onClick={handleBack} sx={{ bgcolor: "white", boxShadow: "0 2px 4px rgba(0,0,0,0.05)", "&:hover": { bgcolor: "#f1f5f9" } }}>
                                <ArrowBackIcon />
                            </IconButton>
                            <Typography variant="button" sx={{ fontWeight: 600, color: "#64748b" }}>
                                {explorerPath === "root" ? "Back to Calendar" : "Back to Calendar"}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {isLoadingFiles ? (
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "200px" }}>
                        {/* <CircularProgress /> */}
                    </Box>
                ) : explorerPath === "root" ? (
                    <Grid container spacing={3}>
                        {folders.map((folder) => (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={folder.id}>
                                <Card
                                    sx={{
                                        cursor: "pointer",
                                        transition: "all 0.3s ease",
                                        "&:hover": {
                                            transform: "translateY(-5px)",
                                            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                                        },
                                        borderRadius: "16px",
                                        border: "1px solid #edf2f7",
                                        textAlign: "center",
                                        p: 3
                                    }}
                                    onClick={() => setExplorerPath(folder.type as any)}
                                >
                                    <Box sx={{ mb: 2 }}>{folder.icon}</Box>
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: "#2d3748" }}>{folder.name}</Typography>
                                    <Typography variant="body2" color="textSecondary">{folder.count} {folder.count === 1 ? 'File' : 'Files'}</Typography>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                ) : (
                    <Grid container spacing={4}>
                        {currentFiles.length > 0 ? (
                            currentFiles.map((file, idx) => (
                                <Grid item xs={12} sm={6} key={idx}>
                                    <Paper elevation={0} sx={{ p: 3, borderRadius: "16px", border: "1px solid #e2e8f0", display: "flex", alignItems: "center", gap: 3, transition: "background 0.2s", "&:hover": { bgcolor: "#f7fafc" } }}>
                                        <Box sx={{ p: 2, bgcolor: "white", borderRadius: "12px", boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>{file.icon}</Box>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5, color: "#1a202c" }}>{file.name}</Typography>
                                            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>{file.fileName}</Typography>
                                            <Box sx={{ display: "flex", gap: 1 }}>
                                                <IconButton size="small" sx={{ bgcolor: "#edf2f7" }} onClick={() => window.open(file.url, "_blank")}><OpenInNewIcon fontSize="small" /></IconButton>
                                                {(file as any).downloadable && (
                                                    <IconButton size="small" sx={{ bgcolor: "#edf2f7" }} onClick={() => handleDownload(file)}><DownloadIcon fontSize="small" /></IconButton>
                                                )}
                                            </Box>
                                        </Box>
                                    </Paper>
                                </Grid>
                            ))
                        ) : (
                            <Grid item xs={12}>
                                <Box sx={{ 
                                    display: "flex", 
                                    flexDirection: "column", 
                                    alignItems: "center", 
                                    justifyContent: "center", 
                                    py: 8,
                                    textAlign: "center",
                                    bgcolor: "#f8fafc",
                                    borderRadius: "16px",
                                    border: "2px dashed #e2e8f0"
                                }}>
                                    <Typography variant="h6" sx={{ color: "#64748b", mb: 1, fontWeight: 600 }}>
                                        No files found
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                                        There are no files available in this {explorerPath === "booklet" ? "Booklets" : explorerPath === "notes" ? "Study Notes" : "Video Lesson"} folder yet.
                                    </Typography>
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                )}
            </Box>
        );
    };

    const renderCalendar = () => (
        <Box sx={{ 
            p: isMobile ? 0.5 : 1, 
            bgcolor: "#f9fafb", 
            display: "flex", 
            flexDirection: isMobile ? "column" : "row",
            gap: 1.5, 
            height: isMobile ? "auto" : "calc(100vh - 120px)", 
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
            fontFamily: "Roboto, sans-serif" 
        }}>
            {/* Sidebar */}
            <Box sx={{ 
                width: isMobile ? "100%" : isTablet ? 260 : 300, 
                flexShrink: 0,
                display: "flex", 
                flexDirection: "column", 
                gap: 1,
                height: isMobile ? "auto" : "100%",
                overflowY: isMobile ? "visible" : "auto"
            }}>
                {/* Mini Calendar */}
                <Accordion expanded={expanded === 'calendar'} onChange={handleAccordionChange('calendar')} sx={{ borderRadius: "12px !important", boxShadow: "0 4px 12px rgba(0,0,0,0.05)", "&:before": { display: "none" } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />} sx={{ minHeight: 48, '& .MuiAccordionSummary-content': { my: 0, alignItems: "center", justifyContent: "space-between" } }}>
                        <Typography sx={{ fontWeight: "bold", fontSize: "14px", color: "#3c4043" }}>{currentDate.format("MMM YYYY")}</Typography>
                        <Box sx={{ display: "flex", mr: 1 }}>
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); setCurrentDate(currentDate.subtract(1, "month")); }}><ChevronLeftIcon sx={{ fontSize: 16 }} /></IconButton>
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); setCurrentDate(currentDate.add(1, "month")); }}><ChevronRightIcon sx={{ fontSize: 16 }} /></IconButton>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 1, pt: 0 }}>
                        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "2px", textAlign: "center" }}>
                            {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (<Typography key={i} sx={{ fontSize: "10px", fontWeight: 500, color: "#70757a" }}>{d}</Typography>))}
                            {miniCalDays.map((day, i) => (
                                <Box key={i} onClick={() => day && setSelectedDate(day)} sx={{ height: 35, display: "flex", alignItems: "center", justifyContent: "center", cursor: day ? "pointer" : "default", border: day?.isSame(selectedDate, 'day') ? "2px solid #1a73e8" : "1px solid #ebedef", borderRadius: "4px", bgcolor: day?.isSame(selectedDate, 'day') ? "#e8f0fe" : "white" }}>
                                    {day && <Typography sx={{ fontSize: "11px", color: day.isToday() ? "#1a73e8" : "#3c4043" }}>{day.date()}</Typography>}
                                </Box>
                            ))}
                        </Box>
                    </AccordionDetails>
                </Accordion>

                {/* Class Schedule */}
                <Accordion expanded={expanded === 'classes'} onChange={handleAccordionChange('classes')} sx={{ borderRadius: "12px !important", boxShadow: "0 4px 12px rgba(0,0,0,0.05)", "&:before": { display: "none" }, flex: (expanded === 'classes' && !isMobile) ? 1 : 'none' }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />}>
                        <Typography sx={{ fontWeight: "bold", fontSize: "14px", color: "#3c4043" }}>Library Records</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 2, pt: 0, overflowY: "auto" }}>
                        <List dense sx={{ p: 0 }}>
                            {processedMeetings.filter(m => m.startTime?.isAfter(dayjs().subtract(7, 'days'))).slice(0, 10).map((m, idx) => (
                                <ListItem key={idx} disablePadding sx={{ mb: 1 }}>
                                    <Box onClick={() => handleCalendarMeetingClick(m)} sx={{ width: "100%", p: 1.5, borderRadius: "10px", bgcolor: "#f8f9fa", border: "1px solid #ebedef", borderLeft: "4px solid #1a73e8", cursor: "pointer" }}>
                                        <Typography variant="caption" sx={{ color: "#70757a", fontSize: "10px" }}>{m.startTime?.format("MMM D")}</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "12px", color: "#3c4043" }}>{m.title}</Typography>
                                    </Box>
                                </ListItem>
                            ))}
                        </List>
                    </AccordionDetails>
                </Accordion>
            </Box>

            {/* Main Content Area */}
            <Card sx={{ flex: 1, display: "flex", flexDirection: "column", borderRadius: "12px", boxShadow: "0 4px 12px rgba(0,0,0,0.05)", overflow: "hidden", minHeight: isMobile ? "75vh" : undefined }}>
                {/* Header */}
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: isMobile ? 1 : 2, py: 1, borderBottom: "1px solid #dadce0", minHeight: isMobile ? 48 : 64 }}>
                    <Typography variant={isMobile ? "subtitle1" : "h5"} sx={{ color: "#3c4043", fontWeight: 600 }}>
                        {selectedDate.format(isMobile ? "D MMM YYYY" : "MMMM YYYY")}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <IconButton size="small" onClick={prevWeek}><ChevronLeftIcon /></IconButton>
                        <IconButton size="small" onClick={nextWeek}><ChevronRightIcon /></IconButton>
                        <Button variant={isMobile ? "text" : "outlined"} size="small" onClick={goToToday} 
                            sx={{ textTransform: "none", color: isMobile ? "#1a73e8" : "#3c4043", borderColor: "#dadce0", borderRadius: "4px", px: isMobile ? 0.5 : 2, fontSize: "12px", minWidth: 0 }}>
                            Today
                        </Button>
                    </Box>
                </Box>

                {/* Grid Grid */}
                <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden" }} ref={scrollRef}>
                    {/* Week Header */}
                    <Box sx={{ position: "sticky", top: 0, bgcolor: "#fff", zIndex: 11, display: "flex", borderBottom: "1px solid #dadce0" }}>
                        <Box sx={{ width: isMobile ? 50 : 75, flexShrink: 0, borderRight: "1px solid #ebedef" }} />
                        <Box sx={{ flex: 1, display: "flex" }}>
                            {weekDays.map((day, idx) => (
                                <Box key={idx} sx={{ flex: 1, textAlign: "center", py: isMobile ? 1 : 1.5, borderRight: "1px solid #dadce0" }}>
                                    <Typography sx={{ fontSize: isMobile ? "10px" : "11px", fontWeight: 600, color: day.isToday() ? "#1a73e8" : "#70757a", textTransform: "uppercase" }}>{DAYS_OF_WEEK[day.day()]}</Typography>
                                    <Box sx={{ 
                                        width: isMobile ? 30 : 38, 
                                        height: isMobile ? 30 : 38, 
                                        mx: "auto", 
                                        mt: 0.5, 
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontSize: isMobile ? "16px" : "20px", 
                                        color: day.isToday() ? "#fff" : "#3c4043", 
                                        bgcolor: day.isToday() ? "#1a73e8" : "transparent", 
                                        borderRadius: "50%" 
                                    }}>
                                        {day.date()}
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Box>

                    {/* Time Grid */}
                    <Box sx={{ display: "flex", position: "relative" }}>
                        <Box sx={{ width: isMobile ? 50 : 75, flexShrink: 0, borderRight: "1px solid #ebedef" }}>
                            {HOURS.map(h => {
                                const label = dayjs().hour(h).minute(0).format(isMobile ? "ha" : "h A");
                                return (
                                    <Box key={h} sx={{ height: 48, position: "relative" }}>
                                        <Typography sx={{ 
                                            position: "absolute", 
                                            top: -8, 
                                            right: isMobile ? 4 : 12, 
                                            fontSize: isMobile ? "9px" : "11px", 
                                            color: "#70757a",
                                            textTransform: "uppercase" 
                                        }}>
                                            {label}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                        <Box sx={{ flex: 1, display: "flex" }}>
                            {weekDays.map((day, dIdx) => (
                                <Box key={dIdx} sx={{ flex: 1, borderRight: "1px solid #dadce0", position: "relative" }}>
                                    {HOURS.map(h => (
                                        <Box key={h} sx={{ height: 48, borderBottom: "1px solid #f1f3f4" }} />
                                    ))}
                                    {processedMeetings.filter(m => m.startTime?.isSame(day, 'day')).map(m => {
                                        const startH = m.startTime!.hour();
                                        const startM = m.startTime!.minute();
                                        const endH = m.endTime!.hour();
                                        const endM = m.endTime!.minute();
                                        const top = startH * 48 + (startM/60) * 48;
                                        const height = Math.max(((endH*60+endM)-(startH*60+startM))/60 * 48 - 2, 20);
                                        return (
                                            <Box key={m.id} onClick={() => handleCalendarMeetingClick(m)} sx={{ position: "absolute", top, left: "4%", width: "92%", height, bgcolor: "#e8f0fe", border: "1px solid #ffffff", borderLeft: "4px solid #1a73e8", boxSizing: "border-box", borderRadius: "4px", p: "4px 8px", zIndex: 10, cursor: "pointer", transition: "all 0.2s", "&:hover": { boxShadow: "0 2px 4px rgba(0,0,0,0.2)" } }}>
                                                <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#1a73e8", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{m.title}</Typography>
                                                <Typography sx={{ fontSize: "10px", color: "#1a73e8" }}>{m.startTime?.format("h:mm A")}</Typography>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Box>
            </Card>
        </Box>
    );

    return (
        <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc" }}>
            {view === "calendar" ? renderCalendar() : renderFiles()}

            {/* Choice Modal */}
            <Dialog open={isChoiceModalOpen} onClose={() => setIsChoiceModalOpen(false)} PaperProps={{ sx: { borderRadius: "20px", width: "100%", maxWidth: 650, p: 3, boxShadow: "0px 10px 40px rgba(0,0,0,0.1)" } }}>
                <DialogTitle sx={{ textAlign: "center", pb: 3, pt: 1 }}>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: "#1a1c1e" }}>Choose Option</Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>Access {meetingForChoice?.title}</Typography>
                </DialogTitle>
                <DialogContent sx={{ overflowX: "hidden", px: 1 }}>
                    <Grid container spacing={3} justifyContent="center" sx={{ mt: 0, mb: 1 }}>
                        <Grid item xs={12} sm={meetingForChoice && meetingForChoice.endTime?.isBefore(dayjs()) ? 4 : 6}>
                            <Card onClick={() => handleChoiceSelection("notes")} sx={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", cursor: "pointer", textAlign: "center", py: 5, px: 2, borderRadius: "16px", transition: "all 0.3s ease", border: "1px solid #ebedef", boxShadow: "0 2px 8px rgba(0,0,0,0.02)", "&:hover": { borderColor: "#1a73e8", bgcolor: "#f8fbff", transform: "translateY(-4px)", boxShadow: "0 8px 24px rgba(26,115,232,0.12)" } }}>
                                <Avatar sx={{ m: "0 auto", bgcolor: "#e8f0fe", color: "#1a73e8", width: 72, height: 72, mb: 3 }}><LibraryBooksIcon sx={{ fontSize: 36 }} /></Avatar>
                                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#3c4043" }}>Study<br/>Materials</Typography>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={meetingForChoice && meetingForChoice.endTime?.isBefore(dayjs()) ? 4 : 6}>
                            <Card onClick={() => handleChoiceSelection("worksheet")} sx={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", cursor: "pointer", textAlign: "center", py: 5, px: 2, borderRadius: "16px", transition: "all 0.3s ease", border: "1px solid #ebedef", boxShadow: "0 2px 8px rgba(0,0,0,0.02)", "&:hover": { borderColor: "#805ad5", bgcolor: "#faf5ff", transform: "translateY(-4px)", boxShadow: "0 8px 24px rgba(128,90,213,0.12)" } }}>
                                <Avatar sx={{ m: "0 auto", bgcolor: "#faf5ff", color: "#805ad5", width: 72, height: 72, mb: 3 }}><AssignmentIcon sx={{ fontSize: 36 }} /></Avatar>
                                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#3c4043" }}>Worksheet</Typography>
                            </Card>
                        </Grid>
                        {meetingForChoice && meetingForChoice.endTime?.isBefore(dayjs()) && (
                            <Grid item xs={12} sm={4}>
                                <Card onClick={() => handleChoiceSelection("video")} sx={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", cursor: "pointer", textAlign: "center", py: 5, px: 2, borderRadius: "16px", transition: "all 0.3s ease", border: "1px solid #ebedef", boxShadow: "0 2px 8px rgba(0,0,0,0.02)", "&:hover": { borderColor: "#ea4335", bgcolor: "#fce8e6", transform: "translateY(-4px)", boxShadow: "0 8px 24px rgba(234,67,53,0.12)" } }}>
                                    <Avatar sx={{ m: "0 auto", bgcolor: "#fce8e6", color: "#ea4335", width: 72, height: 72, mb: 3 }}><PlayCircleOutlineIcon sx={{ fontSize: 36 }} /></Avatar>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#3c4043" }}>Recording</Typography>
                                </Card>
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default MeetingRecordsPage;
